// Top bar — navigation toggle, breadcrumb, ⌘K search, environment badge, task bell (task 355.4).
// F28 (Task 135): the bell shows the pending task count (shared query with the sidebar) and opens /tasks.
// The user menu moved to the navigation panel's user card (UserMenu).
// Task 458.4 (ADR-073 D12): a pill while a GL recalculation job is active (ActiveRecalcBadge).

import React from 'react';
import { Badge } from 'antd';
import { Bell, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSidebarStore } from '../../../stores/sidebarStore';
import { ROUTES } from '../../utils/constants';
import { EnvBadge } from '../ui';
import PrivacyToggle from '../../privacy/PrivacyToggle';
import ActiveRecalcBadge from './ActiveRecalcBadge';
import AppBreadcrumb from './Breadcrumb';
import { usePendingTaskCount } from './useNavGroups';
import styles from './Shell.module.css';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

interface TopBarProps {
  onOpenPalette: () => void;
}

function TopBar({ onOpenPalette }: TopBarProps): React.ReactElement {
  const { collapsed, toggle } = useSidebarStore();
  const navigate = useNavigate();
  const pendingCount = usePendingTaskCount();

  return (
    <header className={styles.top}>
      <button
        type="button"
        className={styles.iconButton}
        onClick={toggle}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
      </button>

      <div className={styles.crumbs}>
        <AppBreadcrumb />
      </div>

      <button type="button" className={styles.search} onClick={onOpenPalette}>
        <Search size={16} aria-hidden="true" />
        <span className={styles.searchText}>Search or jump to…</span>
        <kbd className={styles.kbd}>{IS_MAC ? '⌘K' : 'Ctrl K'}</kbd>
      </button>

      <ActiveRecalcBadge />

      <EnvBadge />

      <PrivacyToggle className={styles.iconButton} />

      <Badge count={pendingCount} showZero={false} overflowCount={99} offset={[-4, 4]}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => navigate(ROUTES.assignments.list)}
          aria-label={
            pendingCount > 0 ? `My assignments — ${pendingCount} active` : 'My assignments'
          }
        >
          <Bell size={18} />
        </button>
      </Badge>
    </header>
  );
}

export default TopBar;
